import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Gamepad2, RefreshCw, Medal } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Juegos conocidos con su enlace para jugar; los nuevos juegos aparecen
// automáticamente en las pestañas apenas registren su primer puntaje.
const JUEGOS_LINKS = [
  { game: 'guajaro', nombre: 'Pescando Cachamas en el Guájaro', href: '/guajaro/index.html' },
  { game: 'cosecha_iprs', nombre: 'Cosecha en el Canal IPRS', href: '/cosecha/index.html' },
  { game: 'comer', nombre: 'Hora de Comer', href: '/comer/index.html' },
  { game: 'oxigeno', nombre: 'Guardián del Oxígeno', href: '/oxigeno/index.html' },
  { game: 'garzas', nombre: '¡Fuera, Garzas!', href: '/garzas/index.html' },
  { game: 'domicilio', nombre: 'Domicilio a Barranquilla', href: '/domicilio/index.html' },
];

const fmtScore = (score, unit) => {
  const n = Math.round(score * 10) / 10;
  const txt = (n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1)).replace('.', ',');
  return unit ? `${txt} ${unit}` : txt;
};

const fmtFecha = (iso) => {
  try {
    // Mostrar la fecha en hora del Este (EDT/EST se ajusta automáticamente)
    return new Date(iso).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'America/New_York',
    });
  } catch (e) {
    return '';
  }
};

const Clasificacion = () => {
  const [juegos, setJuegos] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  const [scores, setScores] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async (game) => {
    setCargando(true);
    try {
      const [gRes, sRes] = await Promise.all([
        axios.get(`${API}/leaderboard/games`),
        axios.get(`${API}/leaderboard`, { params: game && game !== 'todos' ? { game, limit: 50 } : { limit: 50 } }),
      ]);
      setJuegos(gRes.data || []);
      setScores(sRes.data || []);
    } catch (e) {
      setScores([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(filtro); }, [filtro, cargar]);

  const medalla = (i) => {
    if (i === 0) return <Medal size={18} style={{ color: '#D4A017' }} />;
    if (i === 1) return <Medal size={18} style={{ color: '#8C9BA5' }} />;
    if (i === 2) return <Medal size={18} style={{ color: '#B26A3B' }} />;
    return <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{i + 1}</span>;
  };

  return (
    <>
      <Header />
      <section className="admin" style={{ minHeight: '70vh' }}>
        <div className="wrap">
          <div className="admin-top">
            <div>
              <p className="eyebrow">Juegos de Finca Pescado</p>
              <h2 className="sec" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Trophy size={30} style={{ color: 'var(--acento)' }} /> Clasificación
              </h2>
              <p className="sec-sub">Los mejores puntajes de todos los juegos. Termina una partida, guarda tu nombre y aparece aquí.</p>
            </div>
            <button className="btn btn-stripe" style={{ width: 'auto', marginTop: 0, padding: '10px 18px' }} onClick={() => cargar(filtro)} disabled={cargando}>
              <RefreshCw size={18} className={cargando ? 'animate-spin' : ''} /> Actualizar
            </button>
          </div>

          <div className="opt-row" style={{ margin: '6px 0 18px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className={`chip${filtro === 'todos' ? ' sel' : ''}`} onClick={() => setFiltro('todos')} data-testid="tab-todos">Todos</button>
            {juegos.map((j) => (
              <button key={j.game} className={`chip${filtro === j.game ? ' sel' : ''}`} onClick={() => setFiltro(j.game)} data-testid={`tab-${j.game}`}>
                {j.game_name}
              </button>
            ))}
          </div>

          <div className="admin-tabla-wrap">
            <table className="admin-tabla" style={{ minWidth: 640 }} data-testid="tabla-clasificacion">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Jugador</th>
                  <th>Puntaje</th>
                  {filtro === 'todos' && <th>Juego</th>}
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {cargando && (
                  <tr><td colSpan={filtro === 'todos' ? 5 : 4} style={{ textAlign: 'center', color: 'var(--tinta-2)', padding: 24 }}>Cargando...</td></tr>
                )}
                {!cargando && scores.length === 0 && (
                  <tr><td colSpan={filtro === 'todos' ? 5 : 4} style={{ textAlign: 'center', color: 'var(--tinta-2)', padding: 24 }}>
                    Aún no hay puntajes {filtro !== 'todos' ? 'en este juego' : ''}. ¡Sé el primero en jugar!
                  </td></tr>
                )}
                {!cargando && scores.map((s, i) => (
                  <tr key={s.id}>
                    <td>{medalla(i)}</td>
                    <td style={{ fontWeight: 700 }}>{s.player}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--rio)' }}>{fmtScore(s.score, s.unit)}</td>
                    {filtro === 'todos' && <td>{s.game_name}</td>}
                    <td style={{ color: 'var(--tinta-2)' }}>{fmtFecha(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 28 }}>
            <h3 style={{ marginBottom: 12 }}>¿Listo para entrar a la tabla?</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {JUEGOS_LINKS.map((j) => (
                <a key={j.game} className="btn btn-stripe" style={{ width: 'auto', marginTop: 0 }} href={j.href} target="_blank" rel="noopener noreferrer">
                  <Gamepad2 size={20} /> {j.nombre}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default Clasificacion;
